import React, { useState, useEffect, useCallback, useMemo } from "react";
import Cookies from "universal-cookie";
import axios from "axios";
import "../css/Inicio.css";

const coloresTarjetas = [
  { color: "#4361ee", bg: "#eef0fd" },
  { color: "#2ec4b6", bg: "#e8faf9" },
  { color: "#cb6ea9", bg: "#fbe6f3" },
  { color: "#dc3545", bg: "#fde8e8" },
  { color: "#159272", bg: "#e6f7f3" },
  { color: "#7b2d8b", bg: "#f5e9f7" },
  { color: "#f77f00", bg: "#fff4e6" },
  { color: "#e67e22", bg: "#fdf2e9" },
  { color: "#1a56db", bg: "#e8f0fe" },
  { color: "#0891b2", bg: "#e6f4f7" },
];

function Inicio() {
  const cookies = useMemo(() => new Cookies(), []);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const [nombre, setNombre] = useState("");
  const [perfil, setPerfil] = useState("");
  const [hora, setHora] = useState(new Date());
  const [tarjetas, setTarjetas] = useState([]);
  const [cargandoTarjetas, setCargandoTarjetas] = useState(true);

  useEffect(() => {
    setNombre(cookies.get("nombresApellidos") || "");
    setPerfil(cookies.get("perfil") || "");
    const timer = setInterval(() => setHora(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchAccesos = useCallback(async () => {
    const perfilId = cookies.get("perfilId");
    if (!perfilId) {
      setCargandoTarjetas(false);
      return;
    }

    try {
      const [resModulos, resPermisos] = await Promise.all([
        axios.get(`${baseURL}/api/Modulos`),
        axios.get(`${baseURL}/api/Perfiles/${perfilId}/Permisos`),
      ]);

      const mods = Array.isArray(resModulos.data)
        ? resModulos.data
        : resModulos.data?.data || [];

      const perms = Array.isArray(resPermisos.data)
        ? resPermisos.data
        : resPermisos.data?.data || [];

      const tieneAcceso = (idModulo, idSubmodulo) =>
        perms.some(
          (p) =>
            String(p.id_modulo) === String(idModulo) &&
            String(p.id_submodulo || 0) === String(idSubmodulo || 0) &&
            p.puede_ver === true
        );

      const accesos = [];

      (mods || []).forEach((item) => {
        if (item.id_submodulo === null) {
          if (tieneAcceso(item.id_modulo, null)) {
            accesos.push({
              icono: item.modulo_icono || "fa-circle",
              label: item.modulo_nombre,
              ruta: item.modulo_ruta,
              orden: item.modulo_orden,
            });
          }
        } else {
          if (tieneAcceso(item.id_modulo, item.id_submodulo)) {
            accesos.push({
              icono: item.submodulo_icono || "fa-circle",
              label: item.submodulo_nombre,
              ruta: item.submodulo_ruta,
              orden: item.modulo_orden + (item.submodulo_orden || 0) * 0.01,
            });
          }
        }
      });

      accesos.sort((a, b) => a.orden - b.orden);

      const filtradas = accesos.filter(
        (a) =>
          a.ruta &&
          a.ruta !== "/inicio" &&
          a.label?.toLowerCase() !== "inicio"
      );

      const conColores = filtradas.map((t, i) => ({
        ...t,
        ...coloresTarjetas[i % coloresTarjetas.length],
      }));

      setTarjetas(conColores);
    } catch (err) {
      console.error("Error al cargar accesos rápidos:", err);
    } finally {
      setCargandoTarjetas(false);
    }
  }, [baseURL, cookies]);

  useEffect(() => {
    fetchAccesos();
  }, [fetchAccesos]);

  const getSaludo = () => {
    const h = hora.getHours();
    if (h >= 5 && h < 12) return "¡Buenos días";
    if (h >= 12 && h < 18) return "¡Buenas tardes";
    return "¡Buenas noches";
  };

  const formatName = (name) => {
    if (!name) return "Usuario";
    return name.toLowerCase().split(" ")
      .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const primerNombre = formatName(nombre).split(" ")[0];

  const fechaFormateada = hora.toLocaleDateString("es-CO", {
    weekday: "long", year: "numeric", month: "long", day: "numeric"
  });

  const horaFormateada = hora.toLocaleTimeString("es-CO", {
    hour: "2-digit", minute: "2-digit", second: "2-digit"
  });

  const iconoClase = (icono) => {
    if (!icono) return "fas fa-circle";
    return icono.startsWith("fa-") ? `fas ${icono}` : `fas fa-${icono}`;
  };

  return (
    <div className="inicio-wrapper">

      {/* ── Hero ─────────────────────────────────────── */}
      <div className="inicio-hero">
        <div className="inicio-hero-content">
          <div className="inicio-hero-left">
            <p className="inicio-saludo">{getSaludo()}, <strong>{primerNombre}!</strong> 👋</p>
            <h1 className="inicio-titulo">Bienvenido a <span>CRM PowerSoft</span></h1>
            <p className="inicio-subtitulo">Tecnología inteligente para gestionar tus clientes.</p>
            <div className="inicio-badge-perfil">
              <i className="fas fa-shield-alt mr-1"></i> {perfil || "Usuario"}
            </div>
          </div>
          <div className="inicio-hero-right">
            <div className="inicio-geo">
              <div className="geo-ring geo-ring-1"></div>
              <div className="geo-ring geo-ring-2"></div>
              <div className="geo-ring geo-ring-3"></div>
              <div className="geo-dot geo-dot-1"></div>
              <div className="geo-dot geo-dot-2"></div>
              <div className="geo-dot geo-dot-3"></div>
              <div className="geo-dot geo-dot-4"></div>
              <div className="geo-line geo-line-1"></div>
              <div className="geo-line geo-line-2"></div>
              <div className="geo-center">
                <i className="fas fa-star"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Fecha y hora ─────────────────────────────── */}
      <div className="inicio-datetime-bar">
        <span><i className="fas fa-calendar-alt mr-2"></i>{fechaFormateada}</span>
        <span className="inicio-clock"><i className="fas fa-clock mr-2"></i>{horaFormateada}</span>
      </div>

      {/* ── Acceso rápido ─────────────────────────────── */}
      <div className="inicio-section">
        <h4 className="inicio-section-title">
          <i className="fas fa-th-large mr-2"></i>Acceso rápido
        </h4>
        <div className="inicio-cards-grid">
          {cargandoTarjetas ? (
            <div style={{ padding: "2rem", color: "#6c757d" }}>
              <i className="fas fa-spinner fa-spin mr-2" /> Cargando accesos...
            </div>
          ) : tarjetas.length > 0 ? (
            tarjetas.map((t, i) => (
              <a href={t.ruta} key={i} className="inicio-card" style={{ "--card-color": t.color, "--card-bg": t.bg }}>
                <div className="inicio-card-icon">
                  <i className={iconoClase(t.icono)}></i>
                </div>
                <span className="inicio-card-label">{t.label}</span>
                <i className="fas fa-arrow-right inicio-card-arrow"></i>
              </a>
            ))
          ) : (
            <span style={{ color: "#6c757d", fontSize: "0.9rem" }}>
              No hay módulos disponibles para tu perfil.
            </span>
          )}
        </div>
      </div>

    </div>
  );
}

export default Inicio;
